import { useMemo } from 'react';
import type { AxiosInstance } from 'axios';
import { useIntl } from 'react-intl';
import { useStore } from 'react-redux';
import {
  attachAuthRetryInterceptor,
  configureAuthRefresh,
} from 'src/store/auth/refreshAccessToken.js';
import type { Store } from 'src/store/configureStore.js';

import { Configuration, createClient, NotificationsApi } from '@spazious/client-backoffice';
import {
  BackofficeProvider as BackofficeClientProvider,
  OverrideClientType,
} from '@spazious/react-backoffice';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

const { REACT_APP_API } = process.env;
// @ts-expect-error
const basePath = new URL(REACT_APP_API).origin;

type ApiClient = ReturnType<typeof createClient>;

// The SDK builds one axios instance and shares it across every API class
// (`new XxxApi(config, undefined, axios)`). We attach both the per-request JWT
// interceptor and the 401-refresh-retry interceptor to that shared instance; it
// is the only axios instance in the app, so this covers every call made through
// the client (the grid-config hooks use this client too).
// This reaches into SDK internals (the generated API classes keep their axios on
// a protected field), so it may return `undefined` if the SDK changes shape —
// the interceptor helpers degrade to a no-op in that case instead of crashing the
// provider at mount.
const getClientAxios = (client: ApiClient): AxiosInstance | undefined =>
  (client.users as unknown as { axios?: AxiosInstance }).axios;

// Attach a request interceptor that reads the JWT access token from the auth store
// *at request time* and sets the `JWT` scheme. Reading per request (instead of baking
// the token into a static header at construction) is what lets the client stay a
// single instance across token rotations: a rotation is reflected on the very next
// request with no re-render, which also preserves the SPAZ2-3082 stale-client 401
// race fix. When there is no token, no `Authorization` header is forced.
// We do NOT use the SDK's built-in `accessToken` option: as of client 4.110.0 its
// internal interceptor still emits the legacy `Token` scheme, so the token must be
// attached at the app level.
const attachAuthRequestInterceptor = (instance: AxiosInstance | undefined, store: Store): void => {
  if (!instance || typeof instance.interceptors?.request?.use !== 'function') {
    return;
  }

  instance.interceptors.request.use(
    (config) => {
      const {
        auth: { token },
      } = store.getState();

      if (token) {
        config.headers.set('Authorization', `JWT ${token}`);
      }

      return config;
    },
    (error: unknown) => Promise.reject(error)
  );
};

export interface AppApiClientProps {
  children?: React.ReactNode;
}

export const AppApiClient: React.FC<AppApiClientProps> = ({ children }) => {
  const intl = useIntl();
  const store = useStore() as Store;

  // Build the client ONCE for the app's lifetime. The access token is deliberately
  // NOT part of the memo key: baking it into a static header rebuilt the client on
  // every ~5-minute SimpleJWT rotation and pushed a new `BackofficeProvider` context.
  // Instead the request interceptor (attached below) reads the token from the auth
  // store per request, so a rotation needs no new client and no re-render.
  // The memo is keyed only on `intl.locale` so `Accept-Language` stays reactive to a
  // (rare, user-initiated) locale switch; a switch produces a fresh instance whose
  // interceptors are re-attached here.
  const apiClient = useMemo(() => {
    const client = createClient({
      basePath,
      headers: {
        'Accept-Language': intl.locale,
      },
    });

    // Register the refresh helper's deps and attach BOTH interceptors synchronously
    // at construction — not in a post-commit mount effect. This closes the small
    // first-render window where child queries could fire before the interceptors
    // existed, so the very first render's requests carry the JWT scheme and can
    // transparently 401-refresh-and-retry. `configureAuthRefresh` is idempotent, and
    // each `attach*` call only adds its interceptor to the instance passed in.
    configureAuthRefresh({ store, apiOrigin: basePath });

    const clientAxios = getClientAxios(client);
    attachAuthRequestInterceptor(clientAxios, store);
    attachAuthRetryInterceptor(clientAxios);

    return {
      ...client,
      notifications: new NotificationsApi(new Configuration({ basePath }), undefined, clientAxios),
    };
  }, [intl.locale, store]);

  return (
    // Interop mirror of the cast in `useApiClient.ts`: the lib augments
    // `OverrideClientType` against its own nested @spazious/client-backoffice
    // copy, nominally incompatible (protected BaseAPI members) with the app's
    // top-level client even though it is the same runtime object. Goes away
    // with the lib (SPAZ2-2916).
    <BackofficeClientProvider client={apiClient as unknown as OverrideClientType}>
      {children}
      {import.meta.env.DEV && (
        <ReactQueryDevtools buttonPosition="bottom-left" initialIsOpen={false} />
      )}
    </BackofficeClientProvider>
  );
};

export default AppApiClient;
